import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ImagePlus, Images, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail, resolveImage } from "@/lib/api";
import MediaLibraryPicker from "@/components/MediaLibraryPicker";

const EMPTY = { title: "", tag: "Recettes", date: "", read_time: "4 min", excerpt: "", image_url: "", content: "" };
const inputClass = "w-full rounded-xl border border-amber-900/20 bg-cream px-4 py-3 text-sm outline-none transition-colors focus:border-amber-deep";

export default function AdminBlogForm() {
  const { slug } = useParams();
  const isEdit = Boolean(slug);
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/blog/${slug}`)
      .then(({ data }) => {
        setForm({
          title: data.title || "",
          tag: data.tag || "Recettes",
          date: data.date || "",
          read_time: data.read_time || "4 min",
          excerpt: data.excerpt || "",
          image_url: data.image_url || "",
          content: (data.content || []).join("\n\n"),
        });
      })
      .catch(() => toast.error("Article introuvable"))
      .finally(() => setLoading(false));
  }, [slug, isEdit]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd);
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image téléversée");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      title: form.title,
      tag: form.tag,
      date: form.date,
      read_time: form.read_time,
      excerpt: form.excerpt,
      image_url: form.image_url,
      content: form.content.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
    };
    try {
      if (isEdit) {
        await api.put(`/admin/blog/${slug}`, payload);
        toast.success("Article mis à jour — déjà en ligne");
      } else {
        await api.post("/admin/blog", payload);
        toast.success("Article publié — visible dans la Gazette");
      }
      navigate("/admin/gazette");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <p data-testid="blog-form-loading" className="py-20 text-center font-serif text-xl text-bark-soft">Chargement…</p>;
  }

  return (
    <div data-testid="admin-blog-form" className="space-y-6">
      <div>
        <Link to="/admin/gazette" data-testid="blog-form-back" className="mb-3 inline-flex items-center gap-2 text-sm font-medium text-bark-soft hover:text-amber-deep">
          <ArrowLeft size={15} /> Retour à la Gazette
        </Link>
        <h1 className="font-serif text-3xl font-semibold text-bark">{isEdit ? "Modifier l'article" : "Nouvel article"}</h1>
      </div>

      <form onSubmit={handleSubmit} className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-5 rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm lg:col-span-2">
          <div>
            <label htmlFor="bf-title" className="mb-1.5 block text-sm font-medium text-bark">Titre *</label>
            <input id="bf-title" data-testid="bf-title" required value={form.title} onChange={set("title")} className={inputClass} placeholder="Les secrets du miel de tilleul" />
          </div>
          <div className="grid gap-5 sm:grid-cols-3">
            <div>
              <label htmlFor="bf-tag" className="mb-1.5 block text-sm font-medium text-bark">Rubrique</label>
              <select id="bf-tag" data-testid="bf-tag" value={form.tag} onChange={set("tag")} className={inputClass}>
                <option value="Recettes">Recettes</option>
                <option value="Bienfaits">Bienfaits</option>
                <option value="Savoir-faire">Savoir-faire</option>
                <option value="Vie du rucher">Vie du rucher</option>
              </select>
            </div>
            <div>
              <label htmlFor="bf-date" className="mb-1.5 block text-sm font-medium text-bark">Date</label>
              <input id="bf-date" data-testid="bf-date" type="date" value={form.date} onChange={set("date")} className={inputClass} />
            </div>
            <div>
              <label htmlFor="bf-readtime" className="mb-1.5 block text-sm font-medium text-bark">Temps de lecture</label>
              <input id="bf-readtime" data-testid="bf-readtime" value={form.read_time} onChange={set("read_time")} className={inputClass} placeholder="4 min" />
            </div>
          </div>
          <div>
            <label htmlFor="bf-excerpt" className="mb-1.5 block text-sm font-medium text-bark">Résumé (affiché sur les cartes)</label>
            <textarea id="bf-excerpt" data-testid="bf-excerpt" rows={2} value={form.excerpt} onChange={set("excerpt")} className={`${inputClass} resize-none`} placeholder="Une ou deux phrases qui donnent envie de lire…" />
          </div>
          <div>
            <label htmlFor="bf-content" className="mb-1.5 block text-sm font-medium text-bark">Contenu de l'article</label>
            <textarea id="bf-content" data-testid="bf-content" rows={12} value={form.content} onChange={set("content")} className={`${inputClass} resize-y`} placeholder="Rédigez votre article. Séparez les paragraphes par une ligne vide." />
            <p className="mt-1 text-xs text-stone-mute">Séparez les paragraphes par une ligne vide.</p>
          </div>
        </div>

        <div className="space-y-5">
          <div className="rounded-2xl border border-amber-800/15 bg-white p-6 shadow-sm">
            <p className="mb-3 text-sm font-medium text-bark">Image de couverture</p>
            {form.image_url ? (
              <img data-testid="bf-image-preview" src={resolveImage(form.image_url)} alt="Aperçu" className="mb-3 aspect-[16/10] w-full rounded-xl border border-amber-900/10 object-cover" />
            ) : (
              <div className="mb-3 flex aspect-[16/10] w-full items-center justify-center rounded-xl border border-dashed border-amber-900/25 bg-cream text-stone-mute">
                <ImagePlus size={32} />
              </div>
            )}
            <label
              data-testid="bf-upload-label"
              className={`flex cursor-pointer items-center justify-center gap-2 rounded-full border-2 border-amber-700/30 px-4 py-2.5 text-sm font-medium text-amber-900 transition-colors hover:border-amber-deep hover:bg-amber-50 ${uploading ? "opacity-60" : ""}`}
            >
              {uploading ? <Loader2 size={15} className="animate-spin" /> : <ImagePlus size={15} />}
              {uploading ? "Téléversement…" : form.image_url ? "Changer l'image" : "Téléverser une image"}
              <input data-testid="bf-upload-input" type="file" accept="image/*" className="hidden" onChange={handleFile} disabled={uploading} />
            </label>
            <button
              type="button"
              data-testid="bf-library-btn"
              onClick={() => setPickerOpen(true)}
              className="mt-2 flex w-full cursor-pointer items-center justify-center gap-2 rounded-full border border-amber-900/15 px-4 py-2.5 text-sm font-medium text-bark-soft transition-colors hover:border-amber-deep hover:text-amber-deep"
            >
              <Images size={15} /> Choisir dans la bibliothèque
            </button>
          </div>
          <MediaLibraryPicker
            open={pickerOpen}
            onClose={() => setPickerOpen(false)}
            onSelect={(url) => setForm((f) => ({ ...f, image_url: url }))}
            defaultTag="blog"
          />
          <button
            data-testid="bf-submit"
            type="submit"
            disabled={saving || uploading}
            className="w-full rounded-full bg-amber-deep py-4 font-semibold text-white shadow-md shadow-amber-900/15 transition-all duration-200 hover:bg-honey active:scale-[0.98] disabled:opacity-60"
          >
            {saving ? "Publication…" : isEdit ? "Enregistrer les modifications" : "Publier l'article"}
          </button>
        </div>
      </form>
    </div>
  );
}
